import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Empty, Input, Modal, Skeleton, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { SearchHit } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Cmd-K global search palette. Mounts once in AdminShell; opens on Ctrl/Cmd+K.
 * Backend hits students / staff / exams / receipts / applications / requisitions
 * and returns typed routes. Arrow keys navigate; Enter opens; Esc closes.
 */
export function CommandPalette() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQ('');
        setCursor(0);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const search = useQuery({
    queryKey: ['search', institution, q],
    queryFn: () => productApi.globalSearch(institution, q),
    enabled: open && q.trim().length > 0,
  });

  const hits: SearchHit[] = useMemo(() => search.data?.hits ?? [], [search.data]);

  useEffect(() => {
    setCursor(0);
  }, [q]);

  const pick = (h: SearchHit) => {
    setOpen(false);
    navigate(h.href);
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(hits.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter' && hits[cursor]) { e.preventDefault(); pick(hits[cursor]); }
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      closable={false}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      width={640}
      style={{ top: 80 }}
    >
      <div style={{ padding: 12, borderBottom: '1px solid #eeeef1' }}>
        <Input
          size="large"
          autoFocus
          bordered={false}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onInputKey}
          placeholder="Search students, staff, exams, receipts…"
          prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
          suffix={<Typography.Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', ui-monospace, monospace", border: '1px solid #eeeef1', padding: '1px 5px', borderRadius: 4 }}>Esc</Typography.Text>}
        />
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto', padding: '8px 0' }}>
        {!q && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Type to search. ↑/↓ to move · Enter to open · Esc to close.
            </Typography.Text>
          </div>
        )}
        {q && search.isLoading && <div style={{ padding: 16 }}><Skeleton active paragraph={{ rows: 3 }} /></div>}
        {q && search.data && hits.length === 0 && !search.isLoading && (
          <Empty description={`No matches for "${q}"`} style={{ padding: 20 }} />
        )}
        {hits.map((h, i) => (
          <div
            key={`${h.kind}-${h.id}`}
            onMouseEnter={() => setCursor(i)}
            onClick={() => pick(h)}
            style={{
              padding: '10px 14px', cursor: 'pointer',
              background: i === cursor ? '#f0f4fa' : 'transparent',
              borderLeft: i === cursor ? '3px solid #2f6ab0' : '3px solid transparent',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color={KIND_TONE[h.kind]}>{h.kind}</Tag>
              <span style={{ fontWeight: 600, color: '#14213d' }}>{h.label}</span>
            </div>
            <div style={{ fontSize: 12, color: '#5b6478', marginTop: 2, marginLeft: 62 }}>{h.sublabel}</div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

const KIND_TONE: Record<SearchHit['kind'], string> = {
  student: 'processing', staff: 'purple', exam: 'success',
  fee_head: 'default', receipt: 'default', application: 'warning', requisition: 'default',
};
